import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Send, Loader2 } from "lucide-react";
import { contactSchema, getFieldErrors, type FieldErrors } from "@/lib/validations";
import { FormError } from "@/components/ui/form-error";
import { useLanguage } from "@/contexts/LanguageContext";

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const { toast } = useToast();
  const { t } = useLanguage();

  const validateField = (field: string, value: string) => {
    const data = { name: field === "name" ? value : name, email: field === "email" ? value : email, company: field === "company" ? value : company, message: field === "message" ? value : message };
    const result = contactSchema.safeParse(data);
    const fieldErrors = getFieldErrors(result);
    setErrors(prev => ({ ...prev, [field]: fieldErrors[field] || "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = contactSchema.safeParse({ name, email, company, message });
    if (!result.success) { setErrors(getFieldErrors(result)); return; }
    setErrors({});
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-contact-email", { body: result.data });
      if (error) throw error;
      toast({ title: t("contact.successTitle"), description: t("contact.successDesc") });
      setName(""); setEmail(""); setCompany(""); setMessage("");
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast({ title: t("contact.errorTitle"), description: t("contact.errorDesc"), variant: "destructive" });
    } finally { setIsSubmitting(false); }
  };

  return (
    <Layout>
      <Helmet>
        <title>Contact HIDNstock — Get in Touch</title>
        <meta name="description" content="Questions about sourcing deadstock, sorted secondhand or remake projects? Contact the HIDNstock team." />
        <link rel="canonical" href="https://hidnstock.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hidnstock.com/contact" />
        <meta property="og:title" content="Contact HIDNstock — Get in Touch" />
        <meta property="og:description" content="Questions about sourcing deadstock, sorted secondhand or remake projects? Contact the HIDNstock team." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <section className="py-24 md:py-32 bg-primary/5">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">{t("contact.title")}</h1>
            <p className="text-xl text-muted-foreground">{t("contact.subtitle")}</p>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="container mx-auto px-4 max-w-2xl">
            <div className="bg-card border border-border rounded-2xl p-8 md:p-12 shadow-sm">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-3 bg-primary/10 rounded-full"><Mail className="h-6 w-6 text-primary" /></div>
                <div>
                  <h2 className="font-display text-2xl font-semibold">{t("contact.getInTouch")}</h2>
                  <p className="text-muted-foreground">{t("contact.getInTouchDesc")}</p>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t("contact.name")}</Label>
                    <Input id="name" type="text" placeholder={t("contact.namePlaceholder")} value={name}
                      onChange={(e) => { setName(e.target.value); if (errors.name) validateField("name", e.target.value); }}
                      onBlur={() => validateField("name", name)} className={errors.name ? "border-destructive" : ""} maxLength={100} />
                    <FormError message={errors.name} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{t("contact.email")}</Label>
                    <Input id="email" type="email" placeholder={t("contact.emailPlaceholder")} value={email}
                      onChange={(e) => { setEmail(e.target.value); if (errors.email) validateField("email", e.target.value); }}
                      onBlur={() => validateField("email", email)} className={errors.email ? "border-destructive" : ""} maxLength={255} />
                    <FormError message={errors.email} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">{t("contact.company")}</Label>
                  <Input id="company" type="text" placeholder={t("contact.companyPlaceholder")} value={company} onChange={(e) => setCompany(e.target.value)} maxLength={100} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">{t("contact.messageLbl")}</Label>
                  <Textarea id="message" placeholder={t("contact.messagePlaceholder")} value={message}
                    onChange={(e) => { setMessage(e.target.value); if (errors.message) validateField("message", e.target.value); }}
                    onBlur={() => validateField("message", message)} className={errors.message ? "border-destructive" : ""} rows={6} maxLength={2000} />
                  <FormError message={errors.message} />
                </div>
                <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("contact.sending")}</>) : (<><Send className="mr-2 h-4 w-4" />{t("contact.sendMessage")}</>)}
                </Button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Contact;
